import { FC, HTMLAttributes, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Alert, Flex, Form, Input, notification, Select, Typography } from 'antd';
import { Formik } from 'formik';
import * as Yup from 'yup';

import InfoIcon from 'assets/images/info.svg?react';
import {
	BloggerUrlForm,
	CreateFormButtons,
	IBloggerForm,
	ISource,
	prepareCreateBloggereData,
	useSourceSlice,
	useUpdateSource,
} from 'entities/Source';
import { EOnboardingStates, EResultTypes, useOnboardingSlice } from 'features/Onboarding';
import { urlValidator } from 'shared/consts';
import { useCommonSlice } from 'shared/hooks';
import { EMainRoutes, EYmEvents, EYmEventsO1, EYmEventsO2 } from 'shared/typings';
import { sendYmEvent } from 'shared/utils';

interface ICreateBloggerSourceFormProps extends HTMLAttributes<HTMLDivElement> {
	isOnboarding?: boolean;
	source?: ISource;
}

export const CreateBloggerSourceForm: FC<ICreateBloggerSourceFormProps> = ({
	className,
	isOnboarding,
	source,
	...props
}) => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const [skipVerification, setSkipVerification] = useState(false);

	const { bloggerSourceTypeOptions, countryOptions, contentThemesOptions } = useCommonSlice();

	const { setModalState, setResultType, setCreatedSource } = useOnboardingSlice();
	const { type, bloggerValues, bloggerUrls, setBloggerValues, clearValues } = useSourceSlice();

	const { addSource, updateSource, addVerification, data, isLoading, isSuccess, isVerificationSuccess } =
		useUpdateSource();

	const schema: Partial<Record<keyof IBloggerForm, Yup.AnySchema>> = {
		name: Yup.string()
			.required(t('Это поле обязательно для заполнения'))
			.max(64, t('Количество символов не должно превышать {{characters}}', { characters: 64 })),
		platform_types: Yup.array().min(1, t('Это поле обязательно для заполнения')),
		countries: Yup.array().min(1, t('Это поле обязательно для заполнения')),
		content_themes: Yup.array().min(1, t('Это поле обязательно для заполнения')),
	};

	bloggerSourceTypeOptions.forEach(({ value }) => {
		['0', '1', '2'].forEach((key) => {
			schema[`${value}_url_${key}` as keyof IBloggerForm] = Yup.string().when(
				'platform_types',
				(platform_types: string[][], schema) => {
					return platform_types[0].includes(value)
						? key === '0'
							? schema
									.required(t('Укажите минимум одну ссылку'))
									.matches(urlValidator, t('Введите корректный URL'))
									.max(4096, t('Количество символов не должно превышать {{characters}}', { characters: 4096 }))
							: schema
									.matches(urlValidator, t('Введите корректный URL'))
									.max(4096, t('Количество символов не должно превышать {{characters}}', { characters: 4096 }))
						: schema;
				},
			);
		});
	});

	const validationSchema = Yup.object(schema);

	useEffect(() => {
		if (!isSuccess) return;

		sendYmEvent({
			event: EYmEvents.SEND_ADD_ADSPACE_SUCCESS,
			o1: [EYmEventsO1.ACCOUNT, EYmEventsO1.PUBLISHER, isOnboarding ? EYmEventsO1.WELCOME_POPUP : EYmEventsO1.ADSPACE],
			o2: EYmEventsO2.ADD_ADSPACE,
			o4: type,
			wi: data?.uuid,
		});

		if (isOnboarding) {
			setCreatedSource(data || null);

			if (skipVerification) {
				clearValues();
				setModalState(EOnboardingStates.FINAL);
				setResultType(EResultTypes.NEED_VERIFICATION);
			} else {
				addVerification({ body: { source_pk: data?.uuid || '' } });
			}
		} else {
			if (skipVerification) {
				clearValues();
				navigate(`/${EMainRoutes.SOURCES}/`);
				notification.open({
					message: t('Требуется подтвердить владение площадкой'),
					description: t(
						'{{sourceName}} создана успешно, но требуется подтвердить ее владение. Без подтверждения ее владения и прохождения модерации, вы не сможете подавать заявки на подключение к офферам.',
						{ sourceName: data?.name },
					),
					type: 'warning',
					placement: 'topRight',
					showProgress: true,
					pauseOnHover: true,
				});
			} else {
				addVerification({ body: { source_pk: data?.uuid || '' } });
			}
		}
	}, [isSuccess]); // eslint-disable-line

	useEffect(() => {
		if (!isVerificationSuccess) return;

		clearValues();

		if (isOnboarding) {
			setModalState(EOnboardingStates.FINAL);
			setResultType(EResultTypes.SUCCESS);
		} else {
			navigate(`/${EMainRoutes.SOURCES}/`);
			notification.open({
				message: t('Площадка создана успешно'),
				description: t(
					'После подтверждения владения {{sourceName}}, последняя автоматически попадет на модерацию и после ее прохождения можно подавать заявки на подключение к офферам.',
					{ sourceName: data?.name },
				),
				type: 'success',
				placement: 'topRight',
				showProgress: true,
				pauseOnHover: true,
			});
		}
	}, [isVerificationSuccess]); // eslint-disable-line

	return (
		<Formik
			initialValues={bloggerValues}
			validationSchema={validationSchema}
			onSubmit={(values) => {
				source?.uuid
					? updateSource({ ...prepareCreateBloggereData(values, type, bloggerUrls), uuid: source.uuid })
					: addSource(prepareCreateBloggereData(values, type, bloggerUrls));
			}}>
			{({ values, errors, touched, handleChange, handleBlur, handleSubmit, setFieldValue }) => {
				return (
					<form onSubmit={handleSubmit}>
						<Flex {...props} vertical gap={24} className={className}>
							<Flex vertical gap={20}>
								<Form.Item
									layout="vertical"
									label={t('Название площадки')}
									required
									validateStatus={errors.name && touched.name ? 'error' : ''}
									help={errors.name && touched.name && errors.name}>
									<Input
										name="name"
										placeholder="Укажите название"
										onChange={(e) => {
											setBloggerValues({ ...bloggerValues, name: e.target.value });
											handleChange(e);
										}}
										onBlur={handleBlur}
										value={values.name}
										disabled={isLoading}
									/>
								</Form.Item>

								<Form.Item
									layout="vertical"
									label={`${t('Тип платформы')} (${t('Макс.')} 5)`}
									required
									validateStatus={errors.platform_types && touched.platform_types ? 'error' : ''}
									help={errors.platform_types && touched.platform_types && errors.platform_types}>
									<Select
										options={bloggerSourceTypeOptions}
										onChange={(e) => {
											setBloggerValues({ ...bloggerValues, platform_types: e });
											setFieldValue('platform_types', e);
										}}
										onBlur={handleBlur}
										value={values.platform_types}
										mode="multiple"
										maxCount={5}
										maxTagCount="responsive"
										showSearch
										optionFilterProp="label"
										placeholder={t('Выберите один или несколько типов')}
										allowClear
										disabled={isLoading}
									/>
								</Form.Item>

								<Form.Item
									layout="vertical"
									label={`${t('URL платформы ')} (${t('Макс. 3 ссылки на платформу')})`}
									required>
									<Flex vertical gap={8}>
										{!values.platform_types.length && (
											<BloggerUrlForm
												values={values}
												handleChange={handleChange}
												handleBlur={handleBlur}
												errors={errors}
												touched={touched}
												setFieldValue={setFieldValue}
											/>
										)}
										{!!values.platform_types.length &&
											bloggerSourceTypeOptions
												.filter((type) => values.platform_types.includes(type.value))
												.map((platformType) => (
													<BloggerUrlForm
														key={platformType.value}
														type={platformType}
														values={values}
														handleChange={handleChange}
														handleBlur={handleBlur}
														errors={errors}
														touched={touched}
														setFieldValue={setFieldValue}
													/>
												))}
									</Flex>
								</Form.Item>

								<Alert
									message={t('Профиль должен быть публичным для просмотра')}
									showIcon
									icon={<InfoIcon width={16} height={16} />}
								/>

								<Form.Item
									layout="vertical"
									label={`${t('Страна аудитории')} (${t('Макс.')} 5)`}
									required
									validateStatus={errors.countries && touched.countries ? 'error' : ''}
									help={errors.countries && touched.countries && errors.countries}>
									<Select
										options={countryOptions}
										onChange={(e) => {
											setBloggerValues({ ...bloggerValues, countries: e });
											setFieldValue('countries', e);
										}}
										onBlur={handleBlur}
										value={values.countries}
										mode="multiple"
										maxCount={5}
										maxTagCount="responsive"
										showSearch
										optionFilterProp="label"
										placeholder={t('Выберите локали')}
										allowClear
										disabled={isLoading}
									/>
									<Typography.Text type="secondary" style={{ marginTop: 2 }}>
										{t('Помогите улучшить инструмент для поиска брендов')}
									</Typography.Text>
								</Form.Item>

								<Form.Item
									layout="vertical"
									label={`${t('Тематика контента')} (${t('Макс.')} 3)`}
									required
									validateStatus={errors.content_themes && touched.content_themes ? 'error' : ''}
									help={errors.content_themes && touched.content_themes && errors.content_themes}>
									<Select
										options={contentThemesOptions}
										onChange={(e) => {
											setBloggerValues({ ...bloggerValues, content_themes: e });
											setFieldValue('content_themes', e);
										}}
										onBlur={handleBlur}
										value={values.content_themes}
										mode="multiple"
										maxCount={3}
										maxTagCount="responsive"
										showSearch
										optionFilterProp="label"
										placeholder={t('Выберите тематики')}
										allowClear
										disabled={isLoading}
									/>
								</Form.Item>
							</Flex>

							<CreateFormButtons
								isOnboarding={isOnboarding}
								isLoading={isLoading}
								errors={errors}
								touched={touched}
								setSkipVerification={setSkipVerification}
							/>
						</Flex>
					</form>
				);
			}}
		</Formik>
	);
};
